/** Modules déléguables du portail admin (alignés sur le menu). */
import { formatMoney } from '../utils/currency';
export const ADMIN_MODULE_KEYS = [
  'dashboard',
  'inscriptions',
  'paiements',
  'apprenants',
  'activites-apprenants',
  'formations',
  'planning',
  'charge-horaire',
  'formateurs',
  'activites-formateurs',
  'ambassadeurs',
  'activites-ambassadeurs',
  'academique',
  'elearning',
  'excellence',
  'documents',
  'logistique',
  'communication',
  'parrainage',
  'wallet',
  'tarifs',
  'site',
  'partenaires-sponsors',
];

export const ADMIN_MODULE_LABELS = {
  dashboard: 'Tableau de bord',
  inscriptions: 'Inscriptions',
  paiements: 'Paiements',
  apprenants: 'Apprenants',
  'activites-apprenants': 'Activités apprenants',
  formations: 'Formations',
  planning: 'Planning',
  'charge-horaire': 'Charge horaire',
  formateurs: 'Formateurs',
  'activites-formateurs': 'Activités formateurs',
  ambassadeurs: 'Ambassadeurs',
  'activites-ambassadeurs': 'Activités ambassadeurs',
  academique: 'Académique',
  elearning: 'E-learning',
  excellence: 'Excellence',
  documents: 'Dossiers',
  logistique: 'Logistique',
  communication: 'Communication',
  parrainage: 'Parrainage',
  wallet: 'Portefeuilles',
  tarifs: 'Tarification',
  site: 'Site & QR',
  'partenaires-sponsors': 'Partenaires & sponsors',
};

/** Ordre d’affichage / redirection post-connexion modérateur. */
export const ADMIN_MODULE_NAV_ORDER = [...ADMIN_MODULE_KEYS];

/** Chemins toujours accessibles dans le portail admin (profil, aide, notifications). */
export const ADMIN_PORTAL_ALWAYS_PATHS = ['/admin/profile', '/admin/aide', '/admin/notifications'];

/** Vue admin → clé module (segments URL). */
export function resolveAdminViewModule(viewKey) {
  const map = {
    dashboard: 'dashboard',
    inscriptions: 'inscriptions',
    paiements: 'paiements',
    apprenants: 'apprenants',
    apprenantDetail: 'apprenants',
    activitesApprenants: 'activites-apprenants',
    activiteApprenantDetail: 'activites-apprenants',
    activitesFormateurs: 'activites-formateurs',
    activiteFormateurDetail: 'activites-formateurs',
    activitesAmbassadeurs: 'activites-ambassadeurs',
    activiteAmbassadeurDetail: 'activites-ambassadeurs',
    formations: 'formations',
    planning: 'planning',
    chargeHoraire: 'charge-horaire',
    formateurs: 'formateurs',
    ambassadeurs: 'ambassadeurs',
    ambassadeurDetail: 'ambassadeurs',
    academique: 'academique',
    elearning: 'elearning',
    excellence: 'excellence',
    documents: 'documents',
    logistique: 'logistique',
    communication: 'communication',
    security: 'security',
    securityActionDetail: 'security',
    parrainage: 'parrainage',
    wallet: 'wallet',
    tarifs: 'tarifs',
    site: 'site',
    partenairesSponsors: 'partenaires-sponsors',
    equipe: 'equipe',
    notifications: null,
    aide: null,
    profile: null,
  };
  return map[viewKey] ?? 'dashboard';
}

export function userHasAdminModule(user, moduleKey) {
  if (!moduleKey) return true;
  if (!user) return false;
  if (user.role === 'ADMINISTRATEUR') return true;
  if (user.role !== 'MODERATEUR') return false;
  const modules = user.adminModules ?? [];
  return modules.includes(moduleKey);
}

/** Clé module pour un item de menu (charge horaire = module délégué distinct). */
export function resolveAdminNavModuleKey(navItemId) {
  if (navItemId === 'charge-horaire') return 'charge-horaire';
  return navItemId;
}

export function filterAdminNavigation(navigation, user) {
  if (user?.role === 'ADMINISTRATEUR') return navigation;
  if (user?.role !== 'MODERATEUR') return navigation;

  const isGrouped = navigation.length > 0 && navigation[0]?.items;
  if (isGrouped) {
    return navigation
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          userHasAdminModule(user, resolveAdminNavModuleKey(item.id)),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }

  return navigation.filter((item) => userHasAdminModule(user, resolveAdminNavModuleKey(item.id)));
}

export function resolveModerateurHomePath(user) {
  if (user?.role !== 'MODERATEUR') return '/admin/dashboard';
  const modules = user.adminModules ?? [];
  const first = ADMIN_MODULE_NAV_ORDER.find((key) => modules.includes(key));
  if (!first) return '/admin/profile';
  return first === 'dashboard' ? '/admin/dashboard' : `/admin/${first}`;
}

/** Affichage d’un bloc du tableau de bord selon les modules délégués. */
export function canSeeDashboardModule(user, moduleKey) {
  return userHasAdminModule(user, moduleKey);
}

/** Carte latérale du hero dashboard : premier module pertinent du modérateur. */
export function resolveDashboardHeroSideCard(user, data) {
  if (!data) return null;

  const m10 = data.module10;
  const fin = m10?.financier ?? data.paiements;

  if (canSeeDashboardModule(user, 'paiements')) {
    const sideLinks = [
      canSeeDashboardModule(user, 'paiements')
        ? { label: 'Paiements', to: '/admin/paiements', icon: 'account_balance' }
        : null,
    ].filter(Boolean);
    return {
      label: 'Encaissé (CDF)',
      value: formatMoney(data.montantPayeCdf, 'CDF'),
      helper: `sur ${formatMoney(data.montantTotalCdf, 'CDF')} facturé (inscriptions payées)`,
      sideLinks,
    };
  }

  if (canSeeDashboardModule(user, 'inscriptions')) {
    return {
      label: 'Inscriptions en attente',
      value: String(data.inscriptionsEnAttentePaiement ?? 0),
      helper: `${data.inscriptionsConfirmees ?? 0} confirmée(s) · ${data.inscriptionsTotal ?? 0} au total`,
      sideLinks: [{ label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' }],
    };
  }

  if (canSeeDashboardModule(user, 'apprenants')) {
    return {
      label: 'Apprenants',
      value: String(m10?.academique?.nombreApprenants ?? data.apprenantsTotal ?? 0),
      helper: 'Au moins un versement enregistré',
      sideLinks: [{ label: 'Apprenants', to: '/admin/apprenants', icon: 'group' }],
    };
  }

  if (canSeeDashboardModule(user, 'formations')) {
    return {
      label: 'Cohortes ouvertes',
      value: String(data.cohortesOuvertes ?? 0),
      helper:
        (data.cohortesPleines ?? 0) > 0
          ? `${data.cohortesPleines} cohorte(s) complète(s)`
          : 'Places disponibles à l’inscription',
      sideLinks: [{ label: 'Formations', to: '/admin/formations', icon: 'school' }],
    };
  }

  if (canSeeDashboardModule(user, 'formateurs')) {
    return {
      label: 'Heures prestées',
      value: `${m10?.rh?.heuresPresteesMois ?? 0} h`,
      helper:
        m10?.rh?.tauxSatisfactionPedagogiquePct != null
          ? `Satisfaction ${m10.rh.tauxSatisfactionPedagogiquePct} %`
          : 'Mois en cours',
      sideLinks: [{ label: 'Formateurs', to: '/admin/formateurs', icon: 'cast_for_education' }],
    };
  }

  if (canSeeDashboardModule(user, 'logistique')) {
    return {
      label: 'Équipements disponibles',
      value: String(m10?.logistique?.equipementsDisponibles ?? 0),
      helper: `${m10?.logistique?.equipementsEnPanne ?? 0} en panne · ${m10?.logistique?.totalArticles ?? 0} au total`,
      sideLinks: [{ label: 'Logistique', to: '/admin/logistique', icon: 'inventory_2' }],
    };
  }

  if (canSeeDashboardModule(user, 'academique')) {
    return {
      label: 'Taux de réussite',
      value:
        m10?.academique?.tauxReussitePct != null ? `${m10.academique.tauxReussitePct} %` : '—',
      helper: `${m10?.academique?.inscriptionsReussies ?? 0} parcours aboutis`,
      sideLinks: [{ label: 'Académique', to: '/admin/academique', icon: 'menu_book' }],
    };
  }

  if (canSeeDashboardModule(user, 'excellence')) {
    return {
      label: 'Classement général',
      value: String(m10?.academique?.classementGeneralTotal ?? 0),
      helper: `Shortlist ${m10?.academique?.classementShortlist ?? 0} / ${m10?.academique?.tailleShortlist ?? '—'}`,
      sideLinks: [{ label: 'Excellence', to: '/admin/excellence', icon: 'emoji_events' }],
    };
  }

  if (canSeeDashboardModule(user, 'parrainage')) {
    return {
      label: 'Bonus parrainage',
      value: formatMoney(fin?.bonusDistribuesMois ?? 0, 'CDF'),
      helper: 'Distribués ce mois',
      sideLinks: [{ label: 'Parrainage', to: '/admin/parrainage', icon: 'groups' }],
    };
  }

  if (canSeeDashboardModule(user, 'documents')) {
    return {
      label: 'Dossiers apprenants',
      value: 'Validation',
      helper: 'Contrôler les pièces et dossiers soumis',
      sideLinks: [{ label: 'Dossiers', to: '/admin/documents', icon: 'folder_shared' }],
    };
  }

  if (canSeeDashboardModule(user, 'site')) {
    return {
      label: 'Site & QR',
      value: 'Public',
      helper: 'Paramètres du site et codes QR',
      sideLinks: [{ label: 'Site & QR', to: '/admin/site', icon: 'public' }],
    };
  }

  if (canSeeDashboardModule(user, 'settings')) {
    return {
      label: 'Devise & taux',
      value: 'CDF',
      helper: 'Paramètres de change et affichage',
      sideLinks: [{ label: 'Devise', to: '/admin/settings', icon: 'settings_suggest' }],
    };
  }

  return null;
}
